import { ImagePlus } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';

import { colorScope } from '@/features/storefront/appearance';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import type { BeforeAfterSection, FocalPoint } from '@/features/storefront/sections/schema';
import { cn } from '@/lib/cn';

/**
 * Two photos of the same thing, compared under a divider the shopper drags.
 *
 * The "after" photo fills the frame and the "before" photo is laid over it,
 * cut off at the divider - so dragging shows more of one and less of the
 * other without either photo moving. The divider is a real slider
 * (`role="slider"`): arrow keys move it in small steps, Page Up and Page Down
 * in large ones, Home and End to either edge. A pointer, mouse or finger,
 * can grab anywhere on the picture, not only the handle.
 *
 * Left off the shop until both photos are in; the customiser says what is
 * missing instead. The `pair` layout draws a second comparison beside the
 * first once both of its photos are in too - two people's results, two rooms -
 * each with its own slider; until then the first stands alone.
 *
 * The `panel` frame sets the heading and the comparisons in a rounded card,
 * painted in the page's own colours with a soft shadow, inside the band - so a
 * coloured band reads as a mount around the photos.
 */

const STEP = 5;
const BIG_STEP = 20;

function clamp(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

export interface BeforeAfterViewProps {
  section: BeforeAfterSection;
  preview: boolean;
  /** The section's heading, drawn by the renderer so it can be typed into. */
  heading?: ReactNode;
  /** Shown in the customiser while a photo is missing. */
  emptyHint?: ReactNode;
}

/** Whether the second comparison of a `pair` is ready to draw. */
export function hasSecondPair(section: BeforeAfterSection): boolean {
  return (
    section.layout === 'pair' && Boolean(section.second_before_image_url && section.second_after_image_url)
  );
}

/** The page's own colours, over the band's - see `colorScope`. */
const PANEL_COLORS = colorScope('background', 'text') ?? {};

/** The comparisons as they are, or set in the `panel` frame's card. */
function Framed({ section, children }: { section: BeforeAfterSection; children: ReactNode }) {
  if (section.frame !== 'panel') return <div>{children}</div>;
  return (
    <div
      data-before-after-panel
      className="p-5 shadow-[0_12px_40px_-16px_rgba(0,0,0,0.25)] sm:p-8 lg:p-10"
      style={{ ...PANEL_COLORS, borderRadius: 'max(var(--storefront-radius), 1rem)' }}
    >
      {children}
    </div>
  );
}

export function BeforeAfterView({ section, preview, heading, emptyHint }: BeforeAfterViewProps) {
  if (!section.before_image_url || !section.after_image_url) {
    return preview ? <>{emptyHint ?? null}</> : null;
  }
  const first = (
    <Comparison
      section={section}
      before={section.before_image_url}
      after={section.after_image_url}
      beforeFocal={section.before_image_focal}
      afterFocal={section.after_image_focal}
      caption={section.caption}
      sizes={hasSecondPair(section) ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 1024px) 66vw, 100vw'}
    />
  );
  if (!hasSecondPair(section)) {
    return (
      <Framed section={section}>
        {heading}
        {first}
      </Framed>
    );
  }
  return (
    <Framed section={section}>
      {heading}
      <div data-before-after-pair className="grid gap-6 md:grid-cols-2">
        {first}
        <Comparison
          section={section}
          before={section.second_before_image_url}
          after={section.second_after_image_url}
          beforeFocal={section.second_before_image_focal}
          afterFocal={section.second_after_image_focal}
          caption={section.second_caption}
          sizes="(min-width: 768px) 50vw, 100vw"
        />
      </div>
    </Framed>
  );
}

/** One comparison: the two photos, the divider and its slider, and a caption. */
function Comparison({
  section,
  before,
  after,
  beforeFocal,
  afterFocal,
  caption,
  sizes,
}: {
  section: BeforeAfterSection;
  before: string;
  after: string;
  beforeFocal: FocalPoint | null;
  afterFocal: FocalPoint | null;
  caption: string;
  sizes: string;
}) {
  const copy = useShopContent();
  const frame = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [position, setPosition] = useState(() => clamp(section.start_position));
  const vertical = section.orientation === 'vertical';

  // The merchant moving the starting point in the customiser moves the divider.
  useEffect(() => {
    setPosition(clamp(section.start_position));
  }, [section.start_position]);

  const moveTo = (event: PointerEvent<HTMLDivElement>) => {
    const box = frame.current?.getBoundingClientRect();
    if (!box) return;
    const length = vertical ? box.height : box.width;
    if (!length) return;
    const offset = vertical ? event.clientY - box.top : event.clientX - box.left;
    setPosition(clamp((offset / length) * 100));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, (current: number) => number> = {
      ArrowLeft: (current) => current - STEP,
      ArrowDown: (current) => (vertical ? current + STEP : current - STEP),
      ArrowRight: (current) => current + STEP,
      ArrowUp: (current) => (vertical ? current - STEP : current + STEP),
      PageDown: (current) => current - BIG_STEP,
      PageUp: (current) => current + BIG_STEP,
      Home: () => 0,
      End: () => 100,
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    setPosition((current) => clamp(move(current)));
  };

  const beforeLabel = section.before_label.trim();
  const afterLabel = section.after_label.trim();
  const rest = 100 - position;
  const chip =
    'pointer-events-none absolute z-10 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white';

  return (
    <figure className="m-0">
      <div
        ref={frame}
        data-before-after={section.orientation}
        className="relative aspect-[4/3] w-full select-none overflow-hidden"
        style={{
          borderRadius: 'var(--storefront-radius)',
          // A horizontal divider still lets the page scroll up and down
          // under a finger, and a vertical one sideways.
          touchAction: vertical ? 'pan-x' : 'pan-y',
          cursor: vertical ? 'row-resize' : 'col-resize',
        }}
        onPointerDown={(event) => {
          dragging.current = true;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          moveTo(event);
        }}
        onPointerMove={(event) => {
          if (dragging.current) moveTo(event);
        }}
        onPointerUp={(event) => {
          dragging.current = false;
          event.currentTarget.releasePointerCapture?.(event.pointerId);
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      >
        <StorefrontImage
          url={after}
          sizes={sizes}
          alt={afterLabel}
          draggable={false}
          className="absolute inset-0 size-full object-cover"
          style={focalStyle(afterFocal)}
        />
        {/* The divider's clip sits on a wrapper: on the photo itself, a
            repositioned photo's zoom would scale the clip off the divider. */}
        <div
          data-before-image
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: vertical ? `inset(0 0 ${rest}% 0)` : `inset(0 ${rest}% 0 0)` }}
        >
          <StorefrontImage
            url={before}
            sizes={sizes}
            alt={beforeLabel}
            draggable={false}
            className="absolute inset-0 size-full object-cover"
            style={focalStyle(beforeFocal)}
          />
        </div>
        {beforeLabel ? (
          <span className={cn(chip, 'start-3 top-3')}>{beforeLabel}</span>
        ) : null}
        {afterLabel ? (
          <span className={cn(chip, vertical ? 'bottom-3 start-3' : 'end-3 top-3')}>{afterLabel}</span>
        ) : null}
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute bg-white shadow',
            vertical ? 'inset-x-0 h-0.5 -translate-y-1/2' : 'inset-y-0 w-0.5 -translate-x-1/2',
          )}
          style={vertical ? { top: `${position}%` } : { left: `${position}%` }}
        />
        <div
          role="slider"
          tabIndex={0}
          aria-label={copy('section.before_after_slider')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={position}
          aria-valuetext={`${position}%`}
          aria-orientation={vertical ? 'vertical' : 'horizontal'}
          onKeyDown={onKeyDown}
          className="absolute z-20 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={vertical ? { top: `${position}%`, left: '50%' } : { left: `${position}%`, top: '50%' }}
        >
          <span aria-hidden className={cn('text-sm font-bold', vertical && 'rotate-90')}>
            ‹›
          </span>
        </div>
      </div>
      {caption.trim() ? (
        <figcaption className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

/** One photo card of the `cards` layout: its picture (or none yet), its tag and label. */
export interface BeforeAfterCard {
  key: string;
  side: 'before' | 'after';
  imageUrl: string;
  imageFocal: FocalPoint | null;
  tag: string;
  label: string;
}

/**
 * The photos the `cards` layout stands in a row: each pair before then after,
 * the second pair once any of its photos or tags is in. With no photo at all
 * - a theme's sample, or the customiser - both pairs stand as empty frames,
 * so the layout can be judged before the merchant has a customer's photos.
 */
export function beforeAfterCards(
  section: BeforeAfterSection,
  labels: { before: string; after: string },
): BeforeAfterCard[] {
  const before = section.before_label.trim() || labels.before;
  const after = section.after_label.trim() || labels.after;
  const first: BeforeAfterCard[] = [
    { key: 'b1', side: 'before', imageUrl: section.before_image_url, imageFocal: section.before_image_focal, tag: section.before_tag, label: before },
    { key: 'a1', side: 'after', imageUrl: section.after_image_url, imageFocal: section.after_image_focal, tag: section.after_tag, label: after },
  ];
  const second: BeforeAfterCard[] = [
    { key: 'b2', side: 'before', imageUrl: section.second_before_image_url, imageFocal: section.second_before_image_focal, tag: section.second_before_tag, label: before },
    { key: 'a2', side: 'after', imageUrl: section.second_after_image_url, imageFocal: section.second_after_image_focal, tag: section.second_after_tag, label: after },
  ];
  const any = (cards: BeforeAfterCard[]) => cards.some((card) => card.imageUrl.trim() || card.tag.trim());
  const nothingYet = !any(first) && !any(second);
  return nothingYet || any(second) ? [...first, ...second] : first;
}

/** Whether the `cards` layout has any photo to show a shopper. */
export function hasBeforeAfterPhoto(section: BeforeAfterSection): boolean {
  return [
    section.before_image_url,
    section.after_image_url,
    section.second_before_image_url,
    section.second_after_image_url,
  ].some((url) => url.trim());
}

/**
 * The `cards` layout: every photo a tall rounded card in a row that slides
 * (see `Rail`, whose arrows stand beside the heading), a small tag on the
 * photo - the before's at its start, the after's at its end - and BEFORE or
 * AFTER under it. A card with no photo yet is a dashed frame saying which.
 */
export function BeforeAfterCards({
  section,
  rail,
}: {
  section: BeforeAfterSection;
  /** Draws the row: the renderer's `Rail`, given the cards as list items. */
  rail: (items: ReactNode[], count: number) => ReactNode;
}) {
  const copy = useShopContent();
  const cards = beforeAfterCards(section, {
    before: copy('section.before_label'),
    after: copy('section.after_label'),
  });
  const items = cards.map((card) => (
    <figure key={card.key} className="m-0" data-before-after-card={card.side}>
      <div
        className="relative aspect-[2/3] overflow-hidden"
        style={{ borderRadius: 'max(var(--storefront-radius), 1rem)', backgroundColor: 'var(--storefront-surface)' }}
      >
        {card.imageUrl ? (
          <StorefrontImage
            url={card.imageUrl}
            sizes="(min-width: 1024px) 25vw, 50vw"
            alt={card.label}
            className="absolute inset-0 size-full object-cover"
            style={focalStyle(card.imageFocal)}
          />
        ) : (
          <div
            className="absolute inset-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed text-center text-sm"
            style={{
              borderRadius: 'max(var(--storefront-radius), 0.75rem)',
              borderColor: 'color-mix(in srgb, var(--storefront-text) 25%, transparent)',
              color: 'var(--storefront-muted)',
            }}
            data-before-after-empty=""
          >
            <ImagePlus className="size-7" aria-hidden />
            <span>{card.label}</span>
          </div>
        )}
        {card.tag.trim() ? (
          <span
            className={cn(
              'absolute top-3 rounded-full bg-white px-3.5 py-1 text-xs font-medium uppercase tracking-wide text-[#0b1220] shadow-sm',
              card.side === 'before' ? 'start-3' : 'end-3',
            )}
          >
            {card.tag}
          </span>
        ) : null}
      </div>
      <figcaption className="mt-4 text-center text-sm font-semibold uppercase tracking-[0.12em]">{card.label}</figcaption>
    </figure>
  ));
  return (
    <div data-before-after-layout="cards">
      {rail(items, items.length)}
      {section.caption.trim() ? (
        <p className="mt-6 text-center text-xs" style={{ color: 'var(--storefront-muted)' }}>
          {section.caption}
        </p>
      ) : null}
    </div>
  );
}
